import type {
    AiTransformationStatus,
    AiTransformationStatusCount,
    AiTransformationSubject,
    AutomationScoreLevel,
} from '../../../data-contract/ai-transformation-strategy';
import type { AiTransformationFilters } from '../../../fetchers/position-management/ai-transformation-fetchers';
import { HIGH_SCORE_LEVELS, OTHER_SCORE_LEVELS } from './score-levels';

/** The backend moves at most this many items per status update. */
export const MAX_IDS_PER_MOVE = 500;

type MoveRead = {
    readonly filters: AiTransformationFilters;
    readonly subject: AiTransformationSubject;
};

/** The reads and writes prioritizing needs, so the steps can be followed without a backend. */
export type StrategyMoves = {
    /** How many items under the filters stand at `status`. */
    readonly count: (input: MoveRead & { readonly status: AiTransformationStatus }) => Promise<number>;
    /** The items under the filters standing at `from` whose `movableTo` lists `to`. */
    readonly listMovable: (
        input: MoveRead & { readonly from: AiTransformationStatus; readonly to: AiTransformationStatus },
    ) => Promise<readonly string[]>;
    /** Moves the items to `status` and resolves with how many the backend moved. */
    readonly move: (
        input: MoveRead & { readonly ids: readonly string[]; readonly status: AiTransformationStatus },
    ) => Promise<number>;
};

/**
 * What one prioritization acts on. A role, level or position names its positions and, through the positions holding
 * them, its tasks; a matrix selection of tasks names tasks alone. Positions listed by id are those the filters cannot
 * single out, such as one position; a task scope of high potential only takes the tasks scored High or Very high.
 */
export type PrioritizeScope = {
    readonly name: string;
    readonly positions?: { readonly filters: AiTransformationFilters; readonly ids?: readonly string[] };
    readonly tasks?: { readonly filters: AiTransformationFilters; readonly onlyHighPotential: boolean };
};

/** What a prioritization moved, which its undo moves back. */
export type PrioritizeMoved = {
    readonly positionIds: readonly string[];
    readonly taskIds: readonly string[];
};

export type PrioritizeResult = PrioritizeMoved & {
    /** The scope's other assessed tasks still idle, which the user may add too. */
    readonly otherTaskCount: number;
};

const NO_IDS: readonly string[] = [];

const chunks = (ids: readonly string[]): readonly (readonly string[])[] =>
    Array.from({ length: Math.ceil(ids.length / MAX_IDS_PER_MOVE) }, (_, index) =>
        ids.slice(index * MAX_IDS_PER_MOVE, (index + 1) * MAX_IDS_PER_MOVE),
    );

const moveAll = async (
    moves: StrategyMoves,
    input: MoveRead & { readonly ids: readonly string[]; readonly status: AiTransformationStatus },
): Promise<void> => {
    for (const ids of chunks(input.ids)) await moves.move({ ...input, ids });
};

const taskFilters = (
    scope: PrioritizeScope,
    levels: readonly AutomationScoreLevel[],
): AiTransformationFilters | undefined =>
    scope.tasks === undefined
        ? undefined
        : { ...scope.tasks.filters, scoreLevel: levels.length === 0 ? scope.tasks.filters.scoreLevel : levels };

const highPotentialFilters = (scope: PrioritizeScope): AiTransformationFilters | undefined =>
    taskFilters(scope, scope.tasks?.onlyHighPotential ? HIGH_SCORE_LEVELS : []);

/**
 * Moves the scope's idle positions to Prioritized, then its idle tasks (of high potential where the scope says so). Only
 * idle items the backend lets the user move are taken, so nothing further along goes back.
 */
export const prioritize = async (moves: StrategyMoves, scope: PrioritizeScope): Promise<PrioritizeResult> => {
    const { positions } = scope;
    const positionIds =
        positions === undefined
            ? NO_IDS
            : (positions.ids ??
              (await moves.listMovable({
                  filters: positions.filters,
                  from: 'IDLE',
                  subject: 'POSITION',
                  to: 'PRIORITIZED',
              })));
    if (positions !== undefined) {
        await moveAll(moves, {
            filters: positions.filters,
            ids: positionIds,
            status: 'PRIORITIZED',
            subject: 'POSITION',
        });
    }

    const tasks = highPotentialFilters(scope);
    const taskIds =
        tasks === undefined
            ? NO_IDS
            : await moves.listMovable({ filters: tasks, from: 'IDLE', subject: 'TASK', to: 'PRIORITIZED' });
    if (tasks !== undefined)
        await moveAll(moves, { filters: tasks, ids: taskIds, status: 'PRIORITIZED', subject: 'TASK' });

    const others = scope.tasks?.onlyHighPotential ? taskFilters(scope, OTHER_SCORE_LEVELS) : undefined;
    const otherTaskCount =
        others === undefined ? 0 : await moves.count({ filters: others, status: 'IDLE', subject: 'TASK' });

    return { otherTaskCount, positionIds, taskIds };
};

/** Moves the scope's other assessed idle tasks, below high potential, to Prioritized; resolves with those it moved. */
export const prioritizeOtherTasks = async (
    moves: StrategyMoves,
    scope: PrioritizeScope,
): Promise<readonly string[]> => {
    const others = scope.tasks?.onlyHighPotential ? taskFilters(scope, OTHER_SCORE_LEVELS) : undefined;
    if (others === undefined) return NO_IDS;
    const ids = await moves.listMovable({ filters: others, from: 'IDLE', subject: 'TASK', to: 'PRIORITIZED' });
    await moveAll(moves, { filters: others, ids, status: 'PRIORITIZED', subject: 'TASK' });

    return ids;
};

/** Moves back to Idle exactly what a prioritization moved. */
export const undoPrioritize = async (
    moves: StrategyMoves,
    scope: PrioritizeScope,
    moved: PrioritizeMoved,
): Promise<void> => {
    if (scope.positions !== undefined) {
        await moveAll(moves, {
            filters: scope.positions.filters,
            ids: moved.positionIds,
            status: 'IDLE',
            subject: 'POSITION',
        });
    }
    if (scope.tasks !== undefined) {
        await moveAll(moves, { filters: scope.tasks.filters, ids: moved.taskIds, status: 'IDLE', subject: 'TASK' });
    }
};

/**
 * Takes the scope's priority away: its prioritized positions and every one of its tasks still prioritized go back to
 * Idle, whatever their potential; what moved further along stays.
 */
export const unprioritize = async (moves: StrategyMoves, scope: PrioritizeScope): Promise<PrioritizeMoved> => {
    const { positions, tasks } = scope;
    const positionIds =
        positions === undefined
            ? NO_IDS
            : (positions.ids ??
              (await moves.listMovable({
                  filters: positions.filters,
                  from: 'PRIORITIZED',
                  subject: 'POSITION',
                  to: 'IDLE',
              })));
    if (positions !== undefined) {
        await moveAll(moves, { filters: positions.filters, ids: positionIds, status: 'IDLE', subject: 'POSITION' });
    }
    const taskIds =
        tasks === undefined
            ? NO_IDS
            : await moves.listMovable({ filters: tasks.filters, from: 'PRIORITIZED', subject: 'TASK', to: 'IDLE' });
    if (tasks !== undefined)
        await moveAll(moves, { filters: tasks.filters, ids: taskIds, status: 'IDLE', subject: 'TASK' });

    return { positionIds, taskIds };
};

/** How far a group of positions is prioritized: none of them, some or all, judged by how many are still idle. */
export type PriorityCoverage = 'full' | 'none' | 'partial';

export const priorityCoverage = (counts: readonly AiTransformationStatusCount[], total: number): PriorityCoverage => {
    const idle = counts.find(({ status }) => status === 'IDLE')?.count ?? 0;
    if (total === 0 || idle >= total) return 'none';

    return idle === 0 ? 'full' : 'partial';
};
